import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Minus, Plus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, money } from "../lib/api";
import { FadeUp } from "../components/Reveal";
import { ArtImage } from "../components/ArtImage";
import { useCart } from "../context/CartContext";

const Row = ({ label, children, testId }) => (
  <div className="flex justify-between gap-4 py-3 border-b border-white/10 text-sm">
    <span className="text-smoke tracking-[0.15em] text-xs font-bold pt-0.5">{label}</span>
    <span className="text-right" data-testid={testId}>{children}</span>
  </div>
);

export default function PrintDetail() {
  const { id } = useParams();
  const [print, setPrint] = useState(null);
  const [qty, setQty] = useState(1);
  const { add } = useCart();

  useEffect(() => {
    api.get(`/prints/${id}`).then((r) => setPrint(r.data)).catch(() => setPrint(false));
  }, [id]);

  if (print === null) return <div className="pt-32 px-5 text-smoke text-sm">Loading…</div>;
  if (print === false) return <div className="pt-32 px-5 text-smoke text-sm" data-testid="print-not-found">Print not found.</div>;

  const soldOut = print.edition_remaining === 0;
  const purchasable = print.price != null && !soldOut;

  return (
    <div className="pt-32 sm:pt-24 pb-20" data-testid="print-detail-page">
      <div className="px-5 sm:px-8">
        <Link to="/prints" data-testid="back-to-prints" className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] text-smoke hover:text-pink transition-colors duration-200 min-h-[44px]">
          <ArrowLeft size={14} /> ALL EDITIONS
        </Link>
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-8 px-5 sm:px-8">
        <FadeUp>
          <div className="relative border border-white/15 p-2">
            {soldOut && (
              <span data-testid="print-sold-out-banner" className="absolute top-5 left-5 z-10 bg-ink border border-pink text-pink text-xs font-extrabold tracking-[0.3em] px-4 py-2">
                SOLD OUT
              </span>
            )}
            <ArtImage art={print} size="lg" eager sizes="(max-width: 1024px) 100vw, 50vw" className="w-full h-auto block" testId="print-detail-image" />
          </div>
        </FadeUp>

        <FadeUp delay={0.1}>
          <h1 className="font-display text-5xl sm:text-6xl tracking-wide leading-none" data-testid="print-title">
            {print.title || "UNTITLED"}
          </h1>
          <p className="mt-3 font-display text-3xl text-bone" data-testid="print-price">
            {money(print.price) || "PRICE TO BE ANNOUNCED"}
          </p>

          <div className="mt-6">
            {print.type !== "digital" && print.edition_size != null && (
              <Row label="EDITION" testId="print-edition">
                {print.edition_remaining != null ? `${print.edition_remaining} / ${print.edition_size} REMAINING` : `EDITION OF ${print.edition_size}`}
              </Row>
            )}
            {print.type === "open" && <Row label="EDITION" testId="print-edition">Open edition — not numbered</Row>}
            {print.type === "digital" && <Row label="FORMAT" testId="print-format">High-resolution digital file</Row>}
            {print.dimensions && <Row label={print.type === "digital" ? "FILE SIZE" : "PRINT SIZE"} testId="print-dimensions">{print.dimensions}</Row>}
            {print.paper && <Row label="PAPER" testId="print-paper">{print.paper}</Row>}
            {(!print.type || print.type === "limited") && <Row label="FINISH" testId="print-signed">Individually numbered + hand-signed</Row>}
          </div>

          {print.description && <p className="mt-6 text-sm text-bone/80 leading-relaxed" data-testid="print-description">{print.description}</p>}

          <div className="mt-6 bg-charcoal/60 border border-white/10 p-4" data-testid="signed-note">
            <p className="text-xs text-smoke leading-relaxed">
              {print.type === "digital"
                ? "Instant digital download for personal display use. No physical item ships. All artwork © Kenny DePrince — resale, redistribution or reproduction prohibited."
                : print.type === "open"
                ? "Open edition print, produced to order. Shipping and delivery details are confirmed with each purchase. All artwork © Kenny DePrince."
                : "Each limited-edition print is individually numbered and hand-signed by the artist — numbered in the form 12 / 50. Sold-out editions are not reprinted. Shipping and delivery details are confirmed with each purchase."}
            </p>
          </div>

          {purchasable ? (
            <div className="mt-8 flex gap-3">
              <div className="flex items-center border border-white/20">
                <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-decrease" aria-label="Decrease quantity" className="w-11 h-12 flex items-center justify-center hover:text-pink transition-colors duration-200">
                  <Minus size={16} />
                </button>
                <span className="w-10 text-center font-display text-xl" data-testid="qty-value">{qty}</span>
                <button
                  onClick={() => setQty(print.edition_remaining ? Math.min(print.edition_remaining, qty + 1) : qty + 1)}
                  data-testid="qty-increase" aria-label="Increase quantity" className="w-11 h-12 flex items-center justify-center hover:text-pink transition-colors duration-200">
                  <Plus size={16} />
                </button>
              </div>
              <button
                onClick={() => { add(print, qty); toast.success("Added to cart"); }}
                data-testid="add-to-cart-button"
                className="flex-1 h-12 bg-bone text-ink text-sm font-extrabold tracking-[0.2em] hover:bg-pink hover:text-white transition-colors duration-200"
              >
                ADD TO CART
              </button>
            </div>
          ) : (
            <Link
              to={`/contact?type=PRINT%20QUESTION&piece=${encodeURIComponent(print.title || "Limited edition print")}`}
              data-testid="print-inquire-button"
              className="mt-8 flex items-center justify-center h-12 border border-white/25 text-sm font-extrabold tracking-[0.2em] hover:border-pink hover:text-pink transition-colors duration-200"
            >
              {soldOut ? "SOLD OUT — ASK ABOUT FUTURE EDITIONS" : "INQUIRE TO PURCHASE"}
            </Link>
          )}
        </FadeUp>
      </div>
    </div>
  );
}
